import React, {
	useCallback,
	useMemo,
	useEffect,
	useState,
	type Dispatch,
	type SetStateAction,
} from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { graphql } from 'react-relay';

// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import SpotlightTarget from '@atlaskit/onboarding/spotlight-target';
import { useIsFhsEnabled } from '@atlaskit/navigation-system/fhs-rollout';
import { CustomTitle } from '@atlaskit/navigation-system/top-nav-items/custom-title';
import { SideNavToggleButton } from '@atlaskit/navigation-system/layout/side-nav';
import { TopNavStart, TopNavMiddle, TopNavEnd } from '@atlaskit/navigation-system/layout/top-nav';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { serveChatConvergenceExperiment } from '@atlassian/search-page/chat-convergence-experiment';
import { Nav4CollapseReminderPopover } from '@confluence/nav4-onboarding/entry-points/Nav4CollapseReminderPopover.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { APP_NAV_GLOBAL_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component.ts';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { APP_NAVIGATION_METRIC } from '@confluence/browser-metrics/entry-points/app-navigation.metric.ts';
import {
	PERFORMANCE_SUBJECT_navigation,
	PERFORMANCE_SUBJECT_navigationFMP,
} from '@confluence/performance/entry-points/constants.ts';
import { PerformanceEnd } from '@confluence/performance/entry-points/PerformanceEnd.ts';
import { EditionAwareness } from '@confluence/edition-awareness';
import { fg } from '@confluence/feature-gating';
import { useIsMultiSelectActive } from '@confluence/content-tree-actions/entry-points/useIsMultiSelectActive.ts';
import { useContentTreeSelectionActions } from '@confluence/content-tree-actions/entry-points/store.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getUniquePageLoadId } from '@confluence/unique-page-load-id/entry-points/UniquePageLoadId.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import type { useCustomTheme } from './theme/useCustomTheme.ts';
import { AppSwitcherButton } from './AppSwitcherButton.tsx';
import { DeveloperButtons } from './DeveloperButtons.tsx';
import { SiteLogoButton } from './SiteLogoButton.tsx';
import { HelpButton } from './HelpButton.tsx';
import { NotificationsButton } from './NotificationsButton.tsx';
import { SettingsButton } from './SettingsButton.tsx';
import { AccountButton } from './AccountButton.tsx';
import { LoginButton } from './LoginButton.tsx';
import { CreateComponent } from './CreateComponent.tsx';
import { SearchBar } from './SearchBar.tsx';
import { RovoButton } from './RovoButton.tsx';
import type { TopNavigationQuery$data } from './__generated__/TopNavigationQuery.graphql.ts';

const TopNavigationQuery = graphql`
	query TopNavigationQuery {
		siteConfiguration {
			customSiteLogo
		}
		siteSettings {
			siteTitle
			showApplicationTitle
		}
		siteDescription {
			logoUrl
		}
		user: confluence_atlassianUser(current: true) {
			id
			displayName
			emails {
				value
				isPrimary
			}
			photos {
				value
			}
		}
	}
`;

type TopNavigationProps = {
	defaultCollapsed: boolean;
	customTheme?: ReturnType<typeof useCustomTheme>;
	isNav4CollapseReminderSpotlightActive: boolean;
	setIsNav4CollapseReminderSpotlightActive: Dispatch<SetStateAction<boolean>>;
	isLimitedNav?: boolean;
};

const CRITERION_BOTS_CONFIG_KEY = 'cc_criterion_bots';

export const TopNavigation = ({
	defaultCollapsed,
	customTheme,
	isNav4CollapseReminderSpotlightActive,
	setIsNav4CollapseReminderSpotlightActive,
	isLimitedNav,
}: TopNavigationProps): React.JSX.Element | null => {
	const { data, loading, error } = useQueryInterop<TopNavigationQuery$data>(TopNavigationQuery);

	if (isUnauthorizedError(error)) {
		markErrorAsHandled(error);
	}

	if (loading) {
		return null;
	}

	return (
		<>
			<TopNavigationInner
				data={data}
				defaultCollapsed={defaultCollapsed}
				customTheme={customTheme}
				isNav4CollapseReminderSpotlightActive={isNav4CollapseReminderSpotlightActive}
				setIsNav4CollapseReminderSpotlightActive={setIsNav4CollapseReminderSpotlightActive}
				isLimitedNav={isLimitedNav}
			/>
			{error && <ErrorDisplay error={error} />}
		</>
	);
};

type TopNavigationInnerProps = {
	data: TopNavigationQuery$data | undefined;
	defaultCollapsed: boolean;
	customTheme?: ReturnType<typeof useCustomTheme>;
	isNav4CollapseReminderSpotlightActive: boolean;
	setIsNav4CollapseReminderSpotlightActive: Dispatch<SetStateAction<boolean>>;
	isLimitedNav?: boolean;
};

const isAtlassianStaff = (data?: TopNavigationQuery$data) => {
	const criterionBots = StatsigConfigurations.getStringList(CRITERION_BOTS_CONFIG_KEY, []);

	return (
		data?.user?.emails?.some(
			(email) =>
				email?.value &&
				(email.value.endsWith('@atlassian.com') ||
					email.value.endsWith('@atlassian.com.invalid') ||
					email.value.endsWith('@usersinbuckets.com') ||
					criterionBots.includes(email.value)),
		) || false
	);
};

const TopNavigationInner = ({
	data,
	defaultCollapsed,
	customTheme,
	isNav4CollapseReminderSpotlightActive,
	setIsNav4CollapseReminderSpotlightActive,
	isLimitedNav,
}: TopNavigationInnerProps) => {
	const isFhsEnabled = useIsFhsEnabled();

	const { isLoggedIn, cloudId, isLicensed } = useSessionData();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isAtlassianStaffMemoized = useMemo(() => isAtlassianStaff(data), [data]);

	const { uuid, isInitial } = getUniquePageLoadId();
	const hasCustomTheme = !!customTheme?.highlightColor || !!customTheme?.backgroundColor;

	const [customLogoSize, setCustomLogoSize] = useState<{ width: number; height: number } | null>(
		null,
	);

	const handleOnLoadCustomLogo = useCallback(
		(event: React.SyntheticEvent<HTMLImageElement, Event>) => {
			const { naturalWidth, naturalHeight } = event.currentTarget;
			setCustomLogoSize({ width: naturalWidth, height: naturalHeight });
		},
		[],
	);

	useEffect(() => {
		// see: https://hello.jira.atlassian.cloud/browse/NAVX-2056
		if (data && isInitial && uuid) {
			const hasCustomSiteTitle =
				!!data.siteSettings?.showApplicationTitle && !!data.siteSettings?.siteTitle;
			const hasCustomLogo =
				!!data.siteConfiguration?.customSiteLogo && !!data.siteDescription?.logoUrl;

			// If there is a custom logo, but we haven't captured its size, do not log the event yet, otherwise continue
			if (hasCustomLogo && !customLogoSize) {
				return;
			}

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'globalNavigation',
					action: 'viewed',
					actionSubject: 'topNav',
					actionSubjectId: 'customTheme',
					attributes: {
						cloudId,
						hasCustomLogo,
						hasCustomSiteTitle,
						hasCustomHighlightColor: !!customTheme?.highlightColor,
						hasCustomBGColor: !!customTheme?.backgroundColor,
						...(hasCustomLogo && customLogoSize ? customLogoSize : undefined),
						...(hasCustomSiteTitle && {
							customSiteTitleLength: data.siteSettings?.siteTitle.length,
						}),
					},
				},
			}).fire();
		}
	}, [cloudId, createAnalyticsEvent, customLogoSize, data, isInitial, customTheme, uuid]);

	const { exitMultiSelect } = useContentTreeSelectionActions();
	const [isMultiSelectActive] = useIsMultiSelectActive();

	const handleMultiSelectOnCollapse = useCallback(() => {
		if (isMultiSelectActive) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: 'globalNavigation',
					action: 'closed',
					actionSubject: 'bulkActionsMenu',
					attributes: {
						method: 'collapseSideNavButton',
					},
				},
			}).fire();
			exitMultiSelect();
		}
	}, [isMultiSelectActive, exitMultiSelect, createAnalyticsEvent]);

	const sideNavToggleButtonComponent = (
		<SideNavToggleButton
			defaultCollapsed={isFhsEnabled ? undefined : defaultCollapsed}
			collapseLabel={formatMessage(i18n.sideNavToggleButtonCollapseLabel)}
			expandLabel={formatMessage(i18n.sideNavToggleButtonExpandLabel)}
			onClick={handleMultiSelectOnCollapse}
		/>
	);

	const shouldShowNewSpotlight =
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		isNav4CollapseReminderSpotlightActive && fg('confluence_frontend_spotlight_ads_migration');

	const isChatConvergenceEnabled =
		serveChatConvergenceExperiment('confluence', { fireExposure: false }).chatSurface !== undefined;
	const sideNavToggleButton = shouldShowNewSpotlight ? (
		<Nav4CollapseReminderPopover
			isVisible={!!isNav4CollapseReminderSpotlightActive}
			setIsNav4CollapseReminderSpotlightActive={setIsNav4CollapseReminderSpotlightActive}
		>
			{sideNavToggleButtonComponent}
		</Nav4CollapseReminderPopover>
	) : (
		<SpotlightTarget name="nav4-spotlight-1">{sideNavToggleButtonComponent}</SpotlightTarget>
	);

	return (
		<>
			<TopNavStart sideNavToggleButton={sideNavToggleButton}>
				<AppSwitcherButton />
				<DeveloperButtons isAtlassianStaff={isAtlassianStaffMemoized} />
				<SiteLogoButton
					siteConfiguration={data?.siteConfiguration}
					siteSettings={data?.siteSettings}
					siteDescription={data?.siteDescription}
					onLoadCustomLogo={handleOnLoadCustomLogo}
				/>
				{data?.siteSettings?.showApplicationTitle ? (
					<CustomTitle>{data?.siteSettings.siteTitle}</CustomTitle>
				) : null}
			</TopNavStart>
			<TopNavMiddle>
				{!isLimitedNav && <SearchBar renderRovoControllers={isChatConvergenceEnabled} />}
				{!isLimitedNav && <CreateComponent />}
			</TopNavMiddle>
			<TopNavEnd>
				<EditionAwareness />
				{!isLimitedNav && <RovoButton renderControllers={!isChatConvergenceEnabled} />}
				{!isLimitedNav && <NotificationsButton />}
				<HelpButton />
				<SettingsButton />
				{isLoggedIn && data?.user ? <AccountButton user={data.user} /> : <LoginButton />}
			</TopNavEnd>
			<ExperienceSuccess name={APP_NAV_GLOBAL_EXPERIENCE} attributes={{ navVersion: '4' }} />
			<PageSegmentLoadEnd
				metric={APP_NAVIGATION_METRIC}
				customData={{
					isAnonymous: !isLoggedIn,
					isLicensed,
					hasCustomTheme,
				}}
			/>
			<PerformanceEnd
				subject={PERFORMANCE_SUBJECT_navigation}
				subjectId="NavigationLoading"
				includeFeatureFlags
				details={nav4Details}
			/>
			<PerformanceEnd
				subject={PERFORMANCE_SUBJECT_navigationFMP}
				subjectId="NavigationFMP"
				includeFeatureFlags
				details={nav4Details}
			/>
		</>
	);
};

const i18n = defineMessages({
	sideNavToggleButtonCollapseLabel: {
		id: 'app-navigation.top-navigation.side-nav-toggle-button-collapse-label',
		defaultMessage: 'Collapse sidebar',
		description:
			'Label to use for side nav toggle button when it will collapse the side bar when clicked. It ends with a bracket character that reflects the keyboard shortcut that will toggle the side bar.',
	},
	sideNavToggleButtonExpandLabel: {
		id: 'app-navigation.top-navigation.side-nav-toggle-button-expand-label',
		defaultMessage: 'Expand sidebar',
		description:
			'Label to use for side nav toggle button when it will expand the side bar when clicked. It ends with a bracket character that reflects the keyboard shortcut that will toggle the side bar.',
	},
});

const nav4Details = { navVersion: '4' };
